"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { HOSTS, isLocalDev } from "rylak-common";
import DevApp from "../_apps/DevApp";
import App from "../_apps/App";
import { hasOidcSession, setLocalStorageToken, setSessionStorageToken } from "../_apps/_authentication/authCore";
import { OidcAuthProvider } from "~/app/_apps/OidcAuthProvider";
import { usePathname } from "next/navigation";
import { useSelectedBackendHost } from "~/hooks/useSelectedBackendHost";
import { getOidcConfig } from "~/configs/oidc";
import { useBrowserSessionAndLocalState } from "rylak-common/src/hooks/useBrowserState.jsx";
import { SESSION_STORE_KEYS } from "~/constants.jsx";

export default function LoggedInPagesLayout({ children }) {
    const AppComponent = useMemo(() => {
        return isLocalDev() ? DevApp : App;
    }, [process]);

    const [userData, setUserData] = useState();
    const [selectedHostValue, setSelectedHostValue, initialized] = useSelectedBackendHost();
    const [localhostEnv, setLocalhostEnv] = useBrowserSessionAndLocalState({
        key: SESSION_STORE_KEYS.LOCAL_HOST_ENV,
    });
    const devApp = isLocalDev();

    useEffect(() => {
        if (!devApp || !selectedHostValue || selectedHostValue == "") {
            return;
        }
        const baseUrl = HOSTS[selectedHostValue].baseUrl;
        fetch(`${baseUrl}/ping`)
            .then((r) => r.json())
            .then(({ environment }) => setLocalhostEnv(environment?.toUpperCase()))
            .catch(() => setLocalhostEnv(null));
    }, [devApp, selectedHostValue]);

    const oidcConfig = useMemo(() => getOidcConfig(devApp ? selectedHostValue : null, devApp ? localhostEnv : null), [selectedHostValue, localhostEnv]);
    // Key by authority so OidcAuthProvider only remounts when the OIDC provider changes
    // (e.g. switching Dev ↔ Prod), not on every initial storage load (undefined → "Dev").
    const oidcKey = oidcConfig?.authority ?? "default";

    if (!initialized) {
        return;
    }

    return (
        <OidcAuthProvider key={oidcKey} oidcConfig={oidcConfig}>
            <OidcWrapper
                AppComponent={AppComponent}
                userData={userData}
                setUserData={setUserData}
                selectedHostValue={selectedHostValue}
                setSelectedHostValue={setSelectedHostValue}
                initialized={initialized}
                oidcConfig={oidcConfig}
            >
                {children}
            </OidcWrapper>
        </OidcAuthProvider>
    );
}

function triggerLogoutCommon() {
    setSessionStorageToken(null);
    setLocalStorageToken(null);
}

function OidcWrapper({ AppComponent, userData, setUserData, selectedHostValue, setSelectedHostValue, initialized, oidcConfig, children }) {
    const triggerOidcLogout = useOidcLogout();
    const hasAnActiveOidcSession = hasOidcSession(oidcConfig);

    function triggerLogout() {
        triggerLogoutCommon();
        if (hasAnActiveOidcSession) {
            triggerOidcLogout();
        } else {
            setUserData(null);
        }
    }
    const pathname = usePathname();
    if (pathname?.startsWith("/newlogin")) {
        return children;
    }

    return (
        <AppComponent
            userData={userData}
            setUserData={setUserData}
            triggerLogout={triggerLogout}
            selectedHostValue={selectedHostValue}
            setSelectedHostValue={setSelectedHostValue}
            initialized={initialized}
            oidcConfig={oidcConfig}
        >
            {children}
        </AppComponent>
    );
}

export function useOidcLogout() {
    const { signoutRedirect } = useAuth();

    return async () => {
        triggerLogoutCommon();
        await signoutRedirect();
    };
}
